import React, { FC, Fragment, useState, ChangeEvent } from 'react';
import { Order, Rating } from 'models/order';
import { Box, ToggleButton, ToggleButtonGroup, Typography, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

type RateOrderContentProps = {
    order: Order;
    onHandleRating: (rating: Rating) => void;
};

const RateOrderContent: FC<RateOrderContentProps> = ({ order, onHandleRating }: RateOrderContentProps) => {
    const { t: translate } = useTranslation(['orders']);
    // Mounted with the dialog content, so this seeds on open; a live order update must not
    // reset the rating being chosen (DD-3231).
    const [rating, setRating] = useState<Rating>(() => order.rating ?? ({} as Rating));

    const chooseRatingOption = (type: string) => {
        const tempObj = Object.assign({}, rating);
        tempObj.satisfied = type === 'satisfied';
        tempObj.notSatisfied = type === 'notSatisfied';
        setRating(tempObj);
        onHandleRating(tempObj);
    };

    const handleReasonChange = (reason: { name: string; value: boolean }) => {
        const index = rating.reasons.findIndex((res) => res.name === reason.name);
        if (index !== -1) {
            const tempReason = JSON.parse(JSON.stringify(reason));
            tempReason.value = !tempReason.value;
            const tempRating = Object.assign({}, rating);
            const tempReasons = Object.assign([], tempRating.reasons);
            tempReasons.splice(index, 1, tempReason);
            tempRating.reasons = tempReasons;
            setRating(tempRating);
            onHandleRating(tempRating);
        }
    };

    const onCommentChangeEvent = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const tempRating = Object.assign({}, rating);
        tempRating.comments = value;
        setRating(tempRating);
        onHandleRating(tempRating);
    };

    return (
        <Fragment>
            <Typography variant="h3" mb={1}>
                {translate('orders:RATE_ORDER_MODAL_MESSAGE')}
            </Typography>
            <Stack spacing={2}>
                <ToggleButton
                    onClick={() => chooseRatingOption('satisfied')}
                    value={rating.satisfied}
                    selected={rating.satisfied}
                    color="primary"
                    aria-label="satisfied"
                    data-test="rate-order-content.satisfied-toggle-button"
                >
                    {translate('orders:SATISFIED')}
                </ToggleButton>
                <ToggleButton
                    onClick={() => chooseRatingOption('notSatisfied')}
                    value={rating.notSatisfied}
                    color="primary"
                    selected={rating.notSatisfied}
                    aria-label="notSatisfied"
                    data-test="rate-order-content.not-satisfied-toggle-button"
                >
                    {translate('orders:NOT_SATISFIED')}
                </ToggleButton>
                {rating.notSatisfied ? (
                    <Fragment>
                        <Typography variant="h6">{translate('orders:WHAT_ARE_YOUR_REMARKS')}</Typography>
                        <Typography>{translate('orders:YOU_MUST_CHOOSE_AT_LEAST_ONE_REMARK')}</Typography>
                        <ToggleButtonGroup value={rating.reasons}>
                            {rating.reasons.map((reason) => (
                                <ToggleButton
                                    sx={{ marginRight: 1, borderLeft: '1px solid #bbbbbb !important' }}
                                    key={reason.name}
                                    value={reason.name}
                                    onClick={() => handleReasonChange(reason)}
                                    color="primary"
                                    selected={reason.value}
                                    data-test={`rate-order-content.reason-${reason.name}-toggle-button`}
                                >
                                    {translate(reason.name)}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Fragment>
                ) : undefined}
                <Box>
                    <TextField
                        minRows={6}
                        multiline
                        value={rating.comments}
                        onChange={onCommentChangeEvent}
                        placeholder={translate('orders:COMMENT')}
                        fullWidth
                        data-test="rate-order-content.comments"
                    />
                </Box>
            </Stack>
        </Fragment>
    );
};
export default RateOrderContent;
